import React from 'react';
import { m } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiUsers, FiTarget, FiHeart, FiZap, FiShield, FiTrendingUp } from 'react-icons/fi';
import { internalUrl, blogArticleUrl, BLOG_SLUG_FREE_WEBSITE_UK } from './siteConfig';
import {
  BRAND_HOOK_SHORT,
  BRAND_LEDE_LONG,
  BRAND_WHY_CHOOSE_ITEMS,
  BRAND_CLOSING,
} from './brandAboutCopy';

const whyIcons = [FiTarget, FiTrendingUp, FiShield, FiHeart];

const AboutSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const values = [
    {
      icon: FiHeart,
      title: 'Passion for Excellence',
      description: 'High attention to detail on every build and launch.'
    },
    {
      icon: FiZap,
      title: 'Security First',
      description: 'Sensible defaults, patches, and structured reviews—not an afterthought.'
    },
    {
      icon: FiShield,
      title: 'Trust & Reliability',
      description: 'Clear communication, realistic timelines, and support you can count on.'
    },
    {
      icon: FiTrendingUp,
      title: 'Results-Driven',
      description: 'Sites structured for clarity, speed, and discoverability.'
    }
  ];

  return (
    <section id="about" className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-4">
        <m.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <m.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-indigo-500/10 to-cyan-500/10 rounded-full text-indigo-600 font-medium mb-6"
          >
            <FiUsers className="mr-2" />
            About Us
          </m.div>

          <m.h2
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.4 }}
            className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6"
          >
            UK web development
            <span className="block gradient-text">with zero upfront build cost</span>
          </m.h2>

          <m.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.6 }}
            className="text-xl text-gray-700 max-w-3xl mx-auto font-medium leading-relaxed"
          >
            {BRAND_HOOK_SHORT}
          </m.p>
          <m.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.75 }}
            className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto mt-5 leading-relaxed"
          >
            For self-employed visitors comparing DIY builders with bespoke work, see our{' '}
            <a
              href={blogArticleUrl(BLOG_SLUG_FREE_WEBSITE_UK)}
              className="text-indigo-600 font-medium hover:underline"
            >
              UK website builders comparison
            </a>{' '}
            and the{' '}
            <a href={internalUrl('tax-calculator')} className="text-indigo-600 font-medium hover:underline">
              website purchases tax calculator
            </a>
            . Planning pages and budgets start from the{' '}
            <a href={internalUrl('services')} className="text-indigo-600 font-medium hover:underline">
              services
            </a>{' '}
            overview and{' '}
            <a href={internalUrl('faq')} className="text-indigo-600 font-medium hover:underline">
              FAQ
            </a>
            .
          </m.p>
        </m.div>

        <div className="grid lg:grid-cols-2 gap-12 items-start mb-20">
          <m.div
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 1.2 }}
          >
            <h3 className="text-3xl font-bold mb-6 text-gray-900">Our story</h3>
            <div className="space-y-5 text-gray-600 leading-relaxed">
              <p className="text-lg text-gray-800 font-medium">{BRAND_LEDE_LONG}</p>
              <h4 className="text-xl font-bold text-gray-900 pt-2">Why choose WebCodeCraft?</h4>
              <ul className="space-y-4 list-none pl-0">
                {BRAND_WHY_CHOOSE_ITEMS.map((item) => (
                  <li key={item.title}>
                    <span className="font-semibold text-gray-900">{item.title}</span>
                    <span className="text-gray-600"> — {item.body}</span>
                  </li>
                ))}
              </ul>
              <p className="text-gray-800 font-medium">{BRAND_CLOSING}</p>
              <p>
                We follow practical accessibility guidance from the{' '}
                <a
                  href="https://www.w3.org/WAI/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 font-medium hover:underline"
                >
                  W3C Web Accessibility Initiative
                </a>{' '}
                so pages stay usable as browsers and standards change.
              </p>
              <p>
                Next: browse the{' '}
                <a href={internalUrl('portfolio')} className="text-indigo-600 font-medium hover:underline">
                  portfolio
                </a>
                ,{' '}
                <a href={internalUrl('services')} className="text-indigo-600 font-medium hover:underline">
                  packages
                </a>
                ,{' '}
                <a href={internalUrl('faq')} className="text-indigo-600 font-medium hover:underline">
                  FAQ
                </a>
                , or{' '}
                <a href={internalUrl('contact')} className="text-indigo-600 font-medium hover:underline">
                  contact
                </a>
                .
              </p>
            </div>
          </m.div>

          <m.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 1.4 }}
            className="relative lg:sticky lg:top-24"
          >
            <div className="bg-gradient-to-br from-indigo-500 to-cyan-500 rounded-2xl p-8 text-white">
              <h4 className="text-2xl font-bold mb-6">Why choose WebCodeCraft?</h4>
              <ul className="space-y-4">
                {BRAND_WHY_CHOOSE_ITEMS.map((item, index) => {
                  const Icon = whyIcons[index] || FiTarget;
                  return (
                    <li key={item.title} className="flex items-start gap-3">
                      <Icon className="mr-1 mt-0.5 text-xl shrink-0" aria-hidden />
                      <div>
                        <span className="font-semibold block">{item.title}</span>
                        <span className="text-white/90 text-sm leading-relaxed">{item.body}</span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </m.div>
        </div>

        <m.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 1.6 }}
          className="mb-20"
        >
          <h3 className="text-3xl font-bold text-center mb-12 text-gray-900">Our values</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <m.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 1.8 + index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-indigo-500 to-cyan-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <value.icon className="text-white text-2xl" />
                </div>
                <h4 className="text-xl font-bold mb-3 text-gray-900">{value.title}</h4>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </m.div>
            ))}
          </div>
        </m.div>

        <m.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 2.0 }}
          className="text-center mt-16"
        >
          <div className="bg-gradient-to-r from-indigo-600 to-cyan-600 rounded-2xl p-6 sm:p-8 md:p-12 text-white">
            <h3 className="text-3xl font-bold mb-4">Ready when you are</h3>
            <p className="text-xl mb-8 opacity-90">
              Tell us what you need—we will outline scope and pricing clearly.
            </p>
            <m.button
              className="btn bg-white text-indigo-600 hover:bg-gray-100 btn-large"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            >
              Get Started Today
            </m.button>
          </div>
        </m.div>
      </div>
    </section>
  );
};

export default AboutSection;
